import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Eyebrow } from "@/components/brand/Eyebrow";
import { Switch } from "@/components/ui/switch";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { toast } from "sonner";
import {
  readConsent,
  writeConsent,
  clearConsent,
  getOrCreateSessionId,
} from "@/lib/consent";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/settings/privacy")({
  head: () => ({
    meta: [
      { title: "Privacy Preferences — Currency Thoughts" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: PrivacySettings,
});

function PrivacySettings() {
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const c = readConsent();
    if (c) {
      setAnalytics(c.analytics);
      setMarketing(c.marketing);
    }
  }, []);

  async function handleSave() {
    const c = writeConsent({ analytics, marketing });
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      await supabase.from("cookie_consents").insert({
        session_id: getOrCreateSessionId(),
        user_id: sessionData.session?.user?.id ?? null,
        consent_essential: true,
        consent_analytics: c.analytics,
        consent_marketing: c.marketing,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
    } catch (err) {
      console.warn(err);
    }
    toast.success("Preferences saved.");
  }

  function handleReset() {
    clearConsent();
    setAnalytics(false);
    setMarketing(false);
    window.dispatchEvent(new Event("ct:consent-reset"));
    toast.success("Preferences cleared. The banner will reappear.");
  }

  return (
    <div className="min-h-screen bg-cream-50">
      <Navbar />
      <section className="px-5 pb-24 pt-28 md:px-10 md:pt-32">
        <div className="mx-auto max-w-2xl">
          <Eyebrow>Privacy</Eyebrow>
          <h1 className="mt-6 font-serif text-4xl font-bold text-navy-900">
            Your cookie preferences
          </h1>
          <p className="mt-4 font-sans text-charcoal-500">
            Essential cookies are always on so the platform can function. You
            can change analytics and marketing preferences at any time.
          </p>

          <div className="mt-10 space-y-6 border border-bronze-600/40 bg-white p-8">
            <Row label="Essential" description="Required for sign-in, security, and saved preferences." locked checked />
            <Row
              label="Analytics"
              description="Helps us understand which pages are useful so we can improve them."
              checked={analytics}
              onChange={setAnalytics}
            />
            <Row
              label="Marketing"
              description="Lets us measure outreach and personalize what you see."
              checked={marketing}
              onChange={setMarketing}
            />

            <div className="flex flex-wrap items-center gap-3 border-t border-bronze-600/30 pt-6">
              <PrimaryButton onClick={handleSave}>Save preferences</PrimaryButton>
              <button
                onClick={handleReset}
                className="font-sans text-xs uppercase tracking-[0.18em] text-charcoal-500 hover:text-navy-800"
              >
                Reset and show banner
              </button>
            </div>
          </div>

          <p className="mt-6 font-sans text-sm text-charcoal-500">
            See our <Link to="/privacy" className="text-bronze-600 hover:text-navy-800">Privacy Policy</Link>{" "}
            for the full details.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function Row({
  label,
  description,
  checked,
  locked = false,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  locked?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div>
        <p className="font-sans text-[11px] font-semibold uppercase tracking-[0.18em] text-navy-900">
          {label}
          {locked && <span className="ml-2 text-bronze-600">Always on</span>}
        </p>
        <p className="mt-1 font-sans text-sm text-charcoal-500">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={locked}
        onCheckedChange={(v) => onChange?.(v)}
        className={locked ? "data-[state=checked]:bg-bronze-600" : ""}
      />
    </div>
  );
}